import {
	ActionReducerMapBuilder,
	createSlice,
	PayloadAction,
} from "@reduxjs/toolkit";

import { searchApi } from "../../../system/redux/api/search-api";
import { searchQmiApi } from "../../../system/redux/api/search-qmi-api";
import { SearchApiResults } from "../../../system/redux/api/interfaces/search-api-results";
import { ResultListAppState } from "../../models/state/result-list-app-state";
import { transformCommunityApiResults } from "../../functions/wrapping/transform-community-api-results";
import { transformQmiApiResults } from "../../functions/wrapping/transform-qmi-api-results";
import { Result } from "../../models/result";

export const resultListSlice = createSlice({
	name: "results",
	initialState: {
		pagination: { currentMatchedPage: 1, currentUnmatchedPage: 1 },
		selectedResult: null,
		allResults: [],
		currentSort: "",

		rte: { status: "", countdownText: "" },
		hotw: { show: false },
	} as ResultListAppState,

	reducers: {
		//Sorting
		setCurrentSort: (state, action: PayloadAction<string>) => {
			state.currentSort = action.payload;

			//When a new sort is chosen go back to the first page.
			state.pagination.currentMatchedPage = 1;
			state.pagination.currentUnmatchedPage = 1;
		},
		clearCurrentSort: (state, action: PayloadAction<null>) => {
			state.currentSort = "";

			//When the sort is cleared go back to the first page.
			state.pagination.currentMatchedPage = 1;
			state.pagination.currentUnmatchedPage = 1;
		},
		//Selected Result
		setSelectedResult: (state, action: PayloadAction<Result>) => {
			state.selectedResult = action.payload;
		},
		clearSelectedResult: (state, action: PayloadAction<null>) => {
			state.selectedResult = null;
		},
		//Pagination
		setCurrentMatchedPage: (state, action: PayloadAction<number>) => {
			state.pagination.currentMatchedPage = action.payload;
		},
		setCurrentUnmatchedPage: (state, action: PayloadAction<number>) => {
			state.pagination.currentUnmatchedPage = action.payload;
		},
	},
	extraReducers: (builder: ActionReducerMapBuilder<ResultListAppState>) => {
		builder.addMatcher(
			//This adds a listener to the getApiResults call, when that is fulfilled this code will be called
			//so we can process the results.
			searchApi.endpoints.getApiResults.matchFulfilled,
			(state, action) => {
				let searchApiResults = action.payload as SearchApiResults;

				try {
					//Wrap Results
					state.allResults = transformCommunityApiResults(
						searchApiResults.CommunityData,
					);

					//RTE
					state.rte.status =
						searchApiResults?.MetaData?.RteData?.Status?.toLowerCase();

					state.rte.countdownText =
						searchApiResults?.MetaData?.RteData?.CountDown;

					const hasHotwBadge = (searchApiResults?.CommunityData || []).some(
						(community) => {
							const badgeList = community?.commBadgeList?.toString() ?? "";
							const params = new URLSearchParams(badgeList);
							const hotwCount = Number(params.get("hotw") ?? 0);
							return Number.isFinite(hotwCount) && hotwCount > 0;
						},
					);
					state.hotw.show = hasHotwBadge;

					// state.rte.logoImage =
					// 	searchApiResults?.MetaData?.RteData?.LogoImagePath;
					// state.rte.tileImage =
					// 	searchApiResults?.MetaData?.RteData?.TileImagePath;

					// state.rte.tileText1 = searchApiResults?.MetaData?.RteData?.TileText1;
					// state.rte.tileText2 = searchApiResults?.MetaData?.RteData?.TileText2;

					//If a query is done reset the current pages to 1
					state.pagination.currentMatchedPage = 1;
					state.pagination.currentUnmatchedPage = 1;
				} catch (exc) {
					console.log("searchApi.endpoints.getResults.matchFulfilled ERROR");
					console.log(exc);
				}
			},
		)

		builder.addMatcher(
			//This adds a listener to the getApiResults call, when that is fulfilled this code will be called
			//so we can process the results.
			searchQmiApi.endpoints.getQmiApiResults.matchFulfilled,
			(state, action) => {
				let searchApiResults = action.payload as SearchApiResults;

				try {
					//Wrap Results
					state.allResults = transformQmiApiResults(
						searchApiResults.HomeData,
					);

					//RTE
					state.rte.status =
						searchApiResults?.MetaData?.RteData?.Status?.toLowerCase();

					state.rte.countdownText =
						searchApiResults?.MetaData?.RteData?.CountDown;

					//TotalHomeCount
					state.totalHomeCount = searchApiResults?.MetaData?.TotalHomeCount;

					//TotalCommunityCount
					state.totalCommunityCount = searchApiResults?.MetaData?.TotalCommunityCount;

					// state.rte.logoImage =
					// 	searchApiResults?.MetaData?.RteData?.LogoImagePath;
					// state.rte.tileImage =
					// 	searchApiResults?.MetaData?.RteData?.TileImagePath;

					// state.rte.tileText1 = searchApiResults?.MetaData?.RteData?.TileText1;
					// state.rte.tileText2 = searchApiResults?.MetaData?.RteData?.TileText2;

					//If a query is done reset the current pages to 1
					state.pagination.currentMatchedPage = 1;
					state.pagination.currentUnmatchedPage = 1;
				} catch (exc) {
					console.log("searchQmiApi.endpoints.getResults.matchFulfilled ERROR");
					console.log(exc);
				}
			},
		)
	},
});

export const {
	//Sorting
	setCurrentSort,
	clearCurrentSort,

	//Selected Result
	setSelectedResult,
	clearSelectedResult,

	//Pagination
	setCurrentMatchedPage,
	setCurrentUnmatchedPage,
} = resultListSlice.actions;

export default resultListSlice.reducer;
